import { useMemo, useState } from "react";

import {
    Column,
    DIALOG_PADDING,
    DialogWrapper,
    Row,
    SearchInput,
    SkeletonRounded,
    Text,
    VerticalScroll,
    repeatElement,
    useAppPalette,
    useTabbableList
} from "@bridgesplit/ui";
import {
    BsMetaUtil,
    CollateralWithMaxQuote,
    isRaydiumMetadata,
    isWhirlpoolMetadata,
    PriceFetchType,
    useMaxQuotesForCollateral
} from "@bridgesplit/abf-react";
import { formatPercent, formatTokenAmount, textContains } from "@bridgesplit/utils";
import { AppDialog } from "app/utils";

import { useUserCollateralForMarket } from "../borrow";
import MarketDialogHeader from "./common";
import { TokenImage } from "../../common";
import { useMarketDialog } from "../util";
import { MarketProps } from "../types";

const ROW_HEIGHT = 50;
const MAX_HEIGHT = 500;

export default function BorrowSelectCollateralDialog() {
    return (
        <DialogWrapper>
            <BorrowSelectCollateralInternal />
        </DialogWrapper>
    );
}

export function BorrowSelectCollateralInternal() {
    const { close, getData } = useMarketDialog();

    const data = getData(AppDialog.BorrowSelectCollateral);
    const { hoverBackground } = useAppPalette();
    const [search, setSearch] = useState<string>("");

    const { data: collateral } = useUserCollateralForMarket(data?.marketProps as MarketProps, {
        includeLpPositions: true,
        supportedOnly: false,
        priceFetchType: PriceFetchType.Twap
    });

    const { data: collateralWithMaxQuotes, isFetching } = useMaxQuotesForCollateral({
        collateral: collateral,
        strategyDuration: data?.strategyDuration,
        principalMint: data?.marketProps?.principalMint,
        priceFetchType: PriceFetchType.Twap
    });

    const tokens = useMemo(() => {
        if (!collateral) return undefined;

        const maxQuotesMap = new Map(collateralWithMaxQuotes?.map((q) => [q.metadata.mint, q.maxQuote]));

        return collateral.map((item) => ({
            ...item,
            maxQuote: maxQuotesMap.get(item.metadata.mint)
        }));
    }, [collateral, collateralWithMaxQuotes]);

    const filteredTokens = useMemo(() => {
        if (!tokens) return undefined;

        const sortedTokens = [...tokens].sort((a, b) => {
            // Sort by maxQuote in descending order, if available
            const aMax = a.maxQuote?.maxBorrow ?? 0;
            const bMax = b.maxQuote?.maxBorrow ?? 0;
            return bMax - aMax;
        });

        return sortedTokens.filter(({ metadata }) =>
            search
                ? metadata.mint === search ||
                  textContains([BsMetaUtil.getName(metadata), BsMetaUtil.getSymbol(metadata)], search)
                : true
        );
    }, [tokens, search]);

    const { activeIndex, itemRefs, containerRef, setActiveIndex } = useTabbableList(() => {
        const token = filteredTokens?.[activeIndex];
        if (!token) return;
        data?.setToken?.(token);
        close();
    }, filteredTokens?.length ?? 0);

    //There is a typing error with the way we fetch max quotes for whirlpools, instead of showing a wrong price for max quotes, we show a message that the max quote is unavailable.
    // If token is a whirlpool and has more than 1 amount (user has more than 1 whirlpool position for that whirpool mint), we show a message that the max quote is unavailable.
    const maxQuoteUnavailable = (token: CollateralWithMaxQuote) => {
        return (isWhirlpoolMetadata(token.metadata) || isRaydiumMetadata(token.metadata)) && token.amount > 1;
    };

    return (
        <Column spacing={2} sx={{ mx: -DIALOG_PADDING }}>
            <MarketDialogHeader padding header="Select collateral" />

            <SearchInput
                forceFocus
                sx={{ px: DIALOG_PADDING }}
                placeholder="Search by symbol or address"
                onChange={(e) => {
                    setSearch(e.target.value);
                    setActiveIndex(0);
                }}
                value={search}
            />

            <VerticalScroll ref={containerRef} maxHeight={MAX_HEIGHT}>
                {!filteredTokens
                    ? repeatElement(<SkeletonRounded sx={{ mx: DIALOG_PADDING, my: 0.5 }} height={ROW_HEIGHT} />, 5)
                    : filteredTokens?.map((token, i) => {
                          const { metadata, maxQuote } = token;

                          return (
                              <Row
                                  ref={(el) => (el ? (itemRefs.current[i] = el) : null)}
                                  key={token.metadata.mint}
                                  sx={{
                                      px: DIALOG_PADDING,
                                      py: 1,
                                      cursor: "pointer",
                                      background: i === activeIndex ? hoverBackground : undefined,
                                      "&:hover": { background: hoverBackground }
                                  }}
                                  onClick={() => {
                                      data?.setToken?.(token);
                                      close();
                                  }}
                                  spaceBetween
                              >
                                  <Row spacing={1.5}>
                                      <TokenImage metadata={metadata} size="md" />
                                      <Column spacing={0.5}>
                                          <Row spacing={1}>
                                              <Text>{BsMetaUtil.getSymbolUnique(metadata)}</Text>
                                          </Row>
                                          {maxQuote ? (
                                              <Text color="caption" variant="body2">
                                                  {formatPercent(maxQuote?.ltv)} LTV
                                              </Text>
                                          ) : null}
                                      </Column>
                                  </Row>
                                  {(() => {
                                      if (!token.usdValue) {
                                          return null;
                                      }
                                      return (
                                          <Column spacing={0.5} alignItems="flex-end" textAlign="right">
                                              <Text>{token.amount}</Text>
                                              {isFetching ? (
                                                  <SkeletonRounded width={80} height={14} />
                                              ) : maxQuote && !maxQuoteUnavailable(token) ? (
                                                  <Text color="caption" variant="body2">
                                                      Max{" "}
                                                      {formatTokenAmount(maxQuote?.maxBorrow ?? 0, {
                                                          symbol: BsMetaUtil.getSymbol(data?.marketProps.token),
                                                          decimals: 0
                                                      })}
                                                  </Text>
                                              ) : maxQuoteUnavailable(token) ? (
                                                  <Text color="caption" variant="body2">
                                                      Max varies by position
                                                  </Text>
                                              ) : (
                                                  <Text color="disabled" variant="body2">
                                                      No offers
                                                  </Text>
                                              )}
                                          </Column>
                                      );
                                  })()}
                              </Row>
                          );
                      })}
            </VerticalScroll>
        </Column>
    );
}
